import { ThemeConsumer } from "@rneui/themed"
import React, { useEffect, useMemo, useState } from "react"
import { ActivityIndicator, BackHandler, FlatList, Keyboard, KeyboardAvoidingView, Platform, Pressable, ScrollView, Text, TouchableOpacity, useWindowDimensions, View } from "react-native"
import { useFormik } from "formik"
import * as Yup from "yup"
import Popover from "react-native-popover-view"

import { getScreenPermission, useAppDispatch, useAppSelector } from "../../../redux/hooks"
import { useAppNavigation, useAppRoute } from "../../../types/@react-navigation/navigation"
import { useKeyborad } from "../../../shared/hooks/useKeyborad"
import { config } from "../../../shared/environment"

import CommonHeader from "../../../shared/commonComponents/commonHeader"
import CommonDialogBox from "../../../shared/commonComponents/commonDialogBox"

import { ZenButton, ZenCheckBox, ZenDropdown, ZenEmptyComponent, ZenIcon, ZenInput, ZenLoader, ZenSearchBar, ZenText, ZenToast } from "zen-core-elements"
import { addZone, editZone, getOneZoneDetails, setStateData, setZoneListRefresh } from "../../../redux/slices/zoneSlice"
import { ZoneFormValue, zonePostPayLoad } from "./zoneModule"
import { checkDuplicatesRedux, pageDirty } from "../../../redux/slices/commonSlice"
import { Code, stateApi } from "../../customers/addEditCustomerModule"
import SellscreenModal from "../../newSellscreen/sellscreenModal"
import CommonService from "../../../shared/services/commonService"
import { CustomModal } from "../../../shared/commonComponents/customModal"
import { shallowEqual } from "react-redux"

const AddUpdateZone = () => {
	const dispatch = useAppDispatch()
	const navigation = useAppNavigation()
	const { isWrite: isSetttingWrite } = useAppSelector(getScreenPermission("SettingStack"), shallowEqual)
	const { width, height } = useWindowDimensions()
	const { params } = useAppRoute("addUpdateZone")
	const keyboardOpen = useKeyborad()
	const country = useAppSelector((state) => state?.common?.getCountry)
	const state = useAppSelector((state) => state?.zoneSlice?.stateData)
	const [loader, setLoader] = useState(true)
	const [zoneDetails, setZoneDetails] = useState<ZoneFormValue>()
	//variable to handle leave popover
	const [leavePopoverVisible, setLeavePopoverVisible] = useState(false)
	//Variable to handle input container width
	const [inputWidth, setInputWidth] = useState<"100%" | "50%">('100%')
	const [dropdownOpen, setDropdownOpen] = useState(false)
	//variable to store country code
	const [countryCode, setCountryCode] = useState<Code[]>([])
	const [stateModal, setStateModal] = useState(false)
	const [selectedCountry, setSelectedCountry] = useState<Code>()
	const [stateDropdown, setStateDropdown] = useState(false)
	const [stateLoader, setStateLoader] = useState(false)
	const [mapedSelectedState, setMapedSelectedState] = useState<Map<number, string>>(new Map())
	const [transparentLoader, setTransparentLoader] = useState(false)
	const [searchText, setSearchText] = useState<string | null>(null)

	//useEffect triggered  when dimention(width) change 
	useEffect(() => {
		if (width > 600) {
			setInputWidth("50%")
		}
		else {
			setInputWidth("100%")
		}
	}, [width])

	useEffect(() => {
		if (params?.id) {
			getOneZone()
		} else {
			setLoader(false)
		}
	}, [params])

	useEffect(() => {
		if (country?.value) {
			let code: Code[] = []
			country?.value?.country?.map((countries) => {
				code?.push({ label: countries?.name, value: countries?.id, name: countries?.name })
			})
			setCountryCode(code)
		}
	}, [country])

	const searchData = useMemo(() => {
		if (searchText) {
			if (state[selectedCountry?.name || ""]?.length) {
				return state[selectedCountry?.name!!]?.filter((item) => item?.label.toLowerCase().includes(searchText?.toLowerCase()))
			}
			else return []
		}
		return state[selectedCountry?.name || ""] || []
	}, [searchText, state, selectedCountry])

	const getOneZone = () => {
		dispatch(getOneZoneDetails({ storeId: config?.storeId, zoneId: params?.id!! })).unwrap().then((response) => {
			if (response?.zoneData) {
				let countryCode = response?.zoneData?.countryData?.length ? response?.zoneData?.countryData?.map((item) => ({ name: item?.name, value: item?.id!!, label: item?.name })) : []
				let stateCode: number[] = []
				let mapState = mapedSelectedState
				response?.zoneData?.stateData?.forEach((item) => {
					stateCode.push(item?.id!!)
					mapState.set(item?.id!!, item?.name)
				})
				setZoneDetails({
					zoneName: response?.zoneData?.zoneName,
					zoneNameExist: "",
					zoneNameLoader: false,
					countryCode,
					stateCode,
					city: response?.zoneData?.cities?.toString() || "",
					zipCode: response?.zoneData?.zipCode?.toString() || ""
				})
				setMapedSelectedState(mapState)
				setLoader(false)
			} else {
				ZenToast({ type: "error", message: "Failed to get zone details" })
			}
		}).catch(() => {
			ZenToast({ type: "error", message: "Failed to get zone details" })
		})
	}

	const formikValidation = Yup.object().shape({
		zoneName: Yup.string().required("Zone name is required")
			.label("zoneName")
			.max(25, "Zone name should not exceed 25 characters."),
		zoneNameExist: Yup.boolean().oneOf([false], "Zone Name is already exist"),
		zoneNameLoader: Yup.boolean().oneOf([false], "Zone Name loader must be false")
	})

	const formik = useFormik({
		initialValues: {
			zoneName: zoneDetails?.zoneName || "",
			zoneNameLoader: zoneDetails?.zoneNameLoader || false,
			zoneNameExist: zoneDetails?.zoneNameExist || "",
			countryCode: zoneDetails?.countryCode || [],
			stateCode: zoneDetails?.stateCode || [],
			city: zoneDetails?.city || "",
			zipCode: zoneDetails?.zipCode || ""
		},
		enableReinitialize: true,
		validationSchema: formikValidation,
		onSubmit: (value: ZoneFormValue) => {
			let payload = {
				zoneName: value?.zoneName,
				countryId: value?.countryCode?.map((item) => item?.value as number),
				stateId: value?.stateCode,
				cities: value?.city ? value?.city?.split(",") : null,
				zipCode: value?.zipCode ? value?.zipCode?.split(",") : null,
				medium: "POS",
				storeId: config?.storeId
			}
			if (!payload?.countryId?.length && !payload?.stateId?.length && !payload?.zipCode?.length) {
				ZenToast({ type: "warning", message: "Please select either country, state, or give zip code details." })
			}
			else {
				if (params?.id) {
					if (!formik?.dirty) {
						ZenToast({ type: "info", message: "You have no changes to save" })
					} else
						updateZone(payload)
				} else {
					createZone(payload)
				}
			}
		}
	})

	const updateZone = (payload: zonePostPayLoad) => {
		setTransparentLoader(true)
		dispatch(editZone({
			storeId: config?.storeId,
			zoneId: params?.id!!,
			data: payload
		})).unwrap().then((response) => {
			if (response?.success) {
				ZenToast({ type: "success", message: "Zone updated successfully" })
				dispatch(setZoneListRefresh())
				navigation.goBack()
			} else {
				ZenToast({ type: "error", message: "Failed to update zone" })
			}
		}).catch(() => {
			ZenToast({ type: "error", message: "Failed to update zone, Please try again later" })
		}).finally(() => {
			setTransparentLoader(false)
		})
	}

	const createZone = (payload: zonePostPayLoad) => {
		setTransparentLoader(true)
		dispatch(addZone({
			storeId: config?.storeId,
			data: payload
		})).unwrap().then((response) => {
			if (response?.success) {
				ZenToast({ type: "success", message: "Zone created successfully" })
				dispatch(setZoneListRefresh())
				navigation.goBack()
			} else {
				ZenToast({ type: "error", message: "Failed to create zone" })
			}
		}).catch(() => {
			ZenToast({ type: "error", message: "Failed to create zone, Please try again later" })
		}).finally(() => {
			setTransparentLoader(false)
		})
	}

	useEffect(() => {
		if (formik?.dirty) {
			dispatch(pageDirty(true))
		}
		else {
			dispatch(pageDirty(false))
		}
	}, [formik?.dirty])

	useEffect(() => {
		const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
			if (formik?.dirty) {
				setLeavePopoverVisible(true)
				return true;
			}
			else {
				navigation.goBack()
			}
			return true;
		});

		return () => backHandler.remove();
	}, [formik]);

	useEffect(() => {
		const duplicateDebounceFunction = setTimeout(() => {
			if (formik?.values?.zoneName != '' && formik?.values?.zoneName.trim() !== zoneDetails?.zoneName?.trim()) {
				formik.setFieldValue("zoneNameLoader", true)
				checkDuplicate()
			}
			else {
				formik.setFieldValue("zoneNameLoader", false)
				formik?.setFieldValue("zoneNameExist", "")
			}
		}, 500);
		return () => clearTimeout(duplicateDebounceFunction);

	}, [formik?.values?.zoneName])

	useEffect(() => {
		if (selectedCountry?.name) {
			getState()
		}
	}, [selectedCountry])

	const getState = async () => {
		if (!state?.[selectedCountry?.name!!]) {
			setStateLoader(true)
			await CommonService?.getStateName(selectedCountry?.name!!).then((response: { states: stateApi[] }) => {
				if (response && response?.states) {
					let states: Code[] = []
					response?.states?.map((value) => {
						states?.push({ label: value?.name, value: value?.id })
					})
					dispatch(setStateData({ countryCode: selectedCountry?.name!!, data: states }))
				}
			}).catch(() => {
				ZenToast({ type: "error", message: "Failed to get the state" })
			}).finally(() => {
				setStateLoader(false)
			})
		}
	}

	const checkDuplicate = () => {
		let data = {
			"value": formik?.values?.zoneName,
			"tableName": "zones",
			"columnName": "zoneName",
			"id": null,
			"storeId": true
		}
		dispatch(checkDuplicatesRedux({ storeId: config?.storeId, data: data })).unwrap().then((response) => {
			let existError = response?.result?.alreadyExists ? "Zone Name already exists" : ""
			formik?.setFieldValue("zoneNameExist", existError)
			formik.setFieldValue("zoneNameLoader", false)
		}).catch((error: any) => {
			ZenToast({ type: "error", message: "Failed to check the duplicate zone name" })
			console.log("check duplicates redux", error)
		})
	}

	//method for popover confirmation 
	const confirmation = (item: { id: number }) => {
		setLeavePopoverVisible(false)
		if (item?.id == 2) {
			navigation?.goBack()
			dispatch(pageDirty(false))
		}
	}

	const getStateValue = () => {
		let data: string[] = []
		mapedSelectedState?.forEach((item) => data.push(" " + item))
		return data.toString()
	}

	const handleStateOnPress = (item: Code) => {
		let data = mapedSelectedState
		if (mapedSelectedState?.has(item?.value as number)) {
			data.delete(item?.value as number)
			const formikData: number[] = [...data]?.map((item) => item[0])
			formik.setFieldValue("stateCode", formikData)
			setMapedSelectedState(data)
		} else {
			data.set(item?.value as number, item?.label)
			const formikData: number[] = [...data]?.map((item) => item[0])
			formik.setFieldValue("stateCode", formikData)
			setMapedSelectedState(data)
		}
	}

	const getValues = useMemo(() => {
		return formik?.values?.countryCode?.map((item) => item?.value)
	}, [formik?.values?.countryCode])

	return (
		<ThemeConsumer>
			{({ theme }) => (
				<KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined} enabled
					onStartShouldSetResponder={() => {
						setDropdownOpen(false)
						Keyboard?.dismiss()
						return false
					}} style={theme?.addEditCustomerStyle?.outerContainer}>
					<View onStartShouldSetResponder={() => { Keyboard.dismiss(); return false }}>
						<CommonHeader
							headerName={params?.id ? "Edit Zone" : "Create Zone"}
							backIcon
							backIconOnpress={() => { formik?.dirty ? setLeavePopoverVisible(true) : navigation?.goBack() }}
							rightComponent={isSetttingWrite ?
								<ZenButton
									title={params?.id ? "Update" : "Save"}
									onPress={() => { formik.handleSubmit(); !formik?.isValid && ZenToast({ type: "info", message: "Please fill the required fields" }) }}
									type="solid"
									buttonStyle={{ backgroundColor: theme?.primary, borderRadius: 10 }}
									titleColor={theme?.light}
								/> : undefined}
						/>
					</View>

					<View style={{ flex: 1, marginVertical: 10 }}>
						{loader ?
							<ZenLoader viewStyle={{ backgroundColor: "transparent", flex: 1 }} color={theme?.primary} lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} /> :
							<ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={[{ flexGrow: 1 }, (Platform.OS === 'ios' && keyboardOpen) ? { paddingBottom: 130 } : (!keyboardOpen && !params?.id) ? { paddingBottom: 130 } : null,]} keyboardShouldPersistTaps="never">
								<View style={{ backgroundColor: theme?.light, marginHorizontal: 10, borderRadius: 5, paddingVertical: 10, flexDirection: "row", alignItems: "flex-start", flexWrap: "wrap", flex: 1 }}>
									<View style={{ width: "100%" }}>
										<ZenInput
											value={formik?.values?.zoneName}
											autoCapitalize={"none"}
											disabled={!isSetttingWrite}
											onChangeText={(text: string) => formik?.setFieldValue("zoneName", text)}
											label={"Zone Name *"}
											labelStyle={theme?.addEditCustomerStyle?.labelStyle}
											onBlur={formik?.handleBlur("zoneName")}
											onEndEditing={() => { formik?.setFieldValue("zoneName", formik?.values?.zoneName?.trim()) }}
											inputStyle={theme?.modifierSetStyle?.inputStyle}
											rightIcon={formik?.values.zoneNameLoader ? <ActivityIndicator size={"small"} color={theme?.primary} /> : <></>}
											errorMessage={formik?.errors?.zoneName && !formik?.isValid && formik?.touched?.zoneName ? formik?.errors?.zoneName : formik?.values?.zoneNameExist || ""}
											errorStyle={{ fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.red, paddingVertical: formik?.errors?.zoneName && !formik?.isValid && formik?.touched?.zoneName ? 10 : formik?.values?.zoneNameExist ? 10 : 0 }}

											type="outlined"
											containerStyle={{ paddingHorizontal: 15, }}
											inputContainerStyle={{ paddingHorizontal: 8 }}
										/>
									</View>
									<View style={[{ width: inputWidth }, Platform.OS == "ios" && { zIndex: 10 }]}>
										<View style={[{ marginHorizontal: 15 }, Platform.OS == "ios" && { zIndex: 10 }]}>
											<ZenDropdown
												title="Countries"
												disabled={!isSetttingWrite}
												titleStyle={theme?.addEditCustomerStyle?.labelStyle}
												items={countryCode as []}
												open={dropdownOpen}
												setOpen={() => { }}
												onPress={() => {
													setDropdownOpen((prev) => !prev)
												}}
												setValue={() => { }}
												value={getValues}
												onSelectItem={(values) => {
													formik?.setFieldValue("countryCode", values)
												}}
												multipleText={formik?.values?.countryCode?.map((item) => (" " + item?.name))?.toString()}
												searchable={true}
												multiple={true}
												autoScroll={true}
												placeholder={"Select countries"}
												listItemLabelStyle={{ fontSize: theme?.smallerFontSize }}
												labelStyle={{ fontSize: theme?.smallerFontSize, color: theme?.dark, fontFamily: theme?.medium }}
												placeholderStyle={{ fontSize: theme?.smallFontSize, color: theme?.labelColor, fontFamily: theme?.medium }}
											/>
										</View>
									</View>
									<View style={{ width: inputWidth }}>
										<Pressable disabled={!isSetttingWrite} style={{ position: "absolute", width: "95%", height: 45, marginTop: 25, alignSelf: "center", borderRadius: 10, zIndex: 10 }}
											onPress={() => {
												setStateModal(true)
											}} />
										<ZenInput
											value={getStateValue()}
											label={"States"}
											labelStyle={theme?.addEditCustomerStyle?.labelStyle}
											inputStyle={theme?.modifierSetStyle?.inputStyle}
											disabled={!isSetttingWrite}
											type="outlined"
											containerStyle={{ paddingHorizontal: 15, }}
											inputContainerStyle={{ paddingHorizontal: 8 }}
											editable={false}
										/>
									</View>
									<View style={{ width: inputWidth }}>
										<ZenInput
											label="City"
											placeholder="Ex: chennai,madurai,..."
											placeholderTextColor={theme?.lightGrey}
											value={formik?.values?.city || ''}
											onChangeText={(text) => {
												formik?.setFieldValue('city', text)
											}}
											labelStyle={theme?.addEditCustomerStyle?.labelStyle}
											inputStyle={theme?.modifierSetStyle?.inputStyle}
											type="outlined"
											containerStyle={{ paddingHorizontal: 15 }}
											inputContainerStyle={{ paddingHorizontal: 8, height: 90 }}
											numberOfLines={10}
											multiline
											disabled={!isSetttingWrite}
											errorMessage={"use comma(,) to separate the city"}
											errorStyle={{ color: theme?.grey, paddingTop: 5 }}
										/>
									</View>
									<View style={{ width: inputWidth }}>
										<ZenInput
											label="Zip Code"
											placeholder="Ex: 987*,95701,95702,..."
											placeholderTextColor={theme?.lightGrey}
											value={formik?.values?.zipCode || ''}
											onChangeText={(text) => {
												formik?.setFieldValue('zipCode', text)
											}}
											disabled={!isSetttingWrite}
											labelStyle={theme?.addEditCustomerStyle?.labelStyle}
											inputStyle={theme?.modifierSetStyle?.inputStyle}
											type="outlined"
											containerStyle={{ paddingHorizontal: 15 }}
											inputContainerStyle={{ paddingHorizontal: 8, height: 90 }}
											numberOfLines={10}
											multiline
											errorMessage={"use comma(,) to separate the zip code"}
											errorStyle={{ color: theme?.grey, paddingTop: 5 }}
										/>
									</View>
								</View>
							</ScrollView>
						}

						<SellscreenModal isModalOpen={stateModal} onCloseRequest={() => setStateModal(false)}>
							<View style={{ maxWidth: width > 900 ? width * 0.30 : width > 600 ? width * 0.60 : width * 0.80, maxHeight: width > 900 ? height * 0.90 : height * 0.40 }}>
								<View style={{
									flexDirection: 'row',
									justifyContent: 'space-between',
									alignItems: 'center',
									paddingBottom: 16,
									borderBottomWidth: 1,
									borderBottomColor: theme?.softSlate,
								}}>
									<ZenText content="Add State" style={{
										fontSize: theme?.mediumFontSize,
										fontFamily: theme?.bold,
										color: theme?.lightDark,
									}} />
									<TouchableOpacity onPress={() => setStateModal(false)}>
										<ZenIcon name='close' type='antdesign' color={theme?.lightDark} size={24} />
									</TouchableOpacity>
								</View>

								<View style={{ paddingTop: 16 }}>
									<View style={[Platform.OS == "ios" && { zIndex: 10 }]}>
										<ZenDropdown
											title="Country"
											titleStyle={theme?.addEditCustomerStyle?.labelStyle}
											items={countryCode as []}
											open={stateDropdown}
											setOpen={() => { }}
											onPress={() => {
												setStateDropdown((prev) => !prev)
											}}
											setValue={() => { }}
											value={selectedCountry?.value || ""}
											onSelectItem={(value) => {
												setSelectedCountry(value as Code)
												setStateDropdown(false)
											}}
											searchable={true}
											multiple={false}
											autoScroll={true}
											placeholder={"Select country"}
											listItemLabelStyle={{ fontSize: theme?.smallerFontSize }}
											labelStyle={{ fontSize: theme?.smallerFontSize, color: theme?.dark, fontFamily: theme?.medium }}
											placeholderStyle={{ fontSize: theme?.smallFontSize, color: theme?.labelColor, fontFamily: theme?.medium }}
										/>
									</View>
									<View style={{ height: height * 0.45, paddingVertical: 15 }}>
										<ZenText content={"State"} style={theme?.addEditCustomerStyle?.labelStyle} />
										<View>
											<ZenSearchBar
												search={searchText || ""}
												setSearch={(prev) => { setSearchText(prev as string) }}
												onCancel={() => { setSearchText(null); Keyboard?.dismiss() }}
												placeholder={"Search by state name"}
												placeholderTextColor={theme.lightDark}
												searchContainerStyle={{
													"backgroundColor": theme.light,
													"height": 45,
													"paddingHorizontal": 10,
													"borderRadius": 20,
													borderWidth: 0.7,
													borderColor: theme?.grey,
													marginVertical: 10,
												}}
												searchInputStyle={{
													top: -3,
													"color": theme.lightDark,
													"fontSize": theme.smallFontSize,
													"fontFamily": theme.medium,
													"paddingLeft": 15
												}}
											/>
										</View>
										{
											stateLoader ?
												<ZenLoader lottie={require('../../../assets/infinityLoader.json')} lottieType='Local' viewStyle={{ backgroundColor: "transparent" }} /> :
												<FlatList
													data={searchData}
													keyExtractor={(_, index) => index?.toString()}
													renderItem={({ item }) => (
														<TouchableOpacity onPress={() => handleStateOnPress(item)} style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
															<ZenText content={item?.label} size={theme?.smallFontSize} style={{ fontFamily: mapedSelectedState?.has(item?.value as number) ? theme?.bold : theme?.medium, color: theme?.lightDark }} />
															<ZenCheckBox
																containerStyle={{ backgroundColor: "transparent" }}
																checkedColor={theme.primary}
																onPress={() => handleStateOnPress(item)}
																checked={mapedSelectedState?.has(item?.value as number)}
																size={24}
															/>
														</TouchableOpacity>
													)}
													contentContainerStyle={{ paddingTop: 10, gap: 10 }}
													ListEmptyComponent={<ZenEmptyComponent viewStyle={{ backgroundColor: theme?.light }} />}
												/>}
									</View>
								</View>
							</View>
						</SellscreenModal>

						<CustomModal visible={transparentLoader} transparent>
							<ZenLoader viewStyle={{ backgroundColor: "transparent" }} color={theme?.primary} lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} />
						</CustomModal>

						{/* For leave confirmation */}
						<Popover isVisible={leavePopoverVisible} popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }}>
							<CommonDialogBox type="EDIT" replaceValue="page" confirmation={confirmation} />
						</Popover>
					</View>
				</KeyboardAvoidingView>
			)}
		</ThemeConsumer>
	)
}

export default AddUpdateZone